import { Delete, Edit, Save } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useNavigation } from "contexts/notificationsContext";
import { useCallback, useEffect, useState } from "react";
import { useAuth } from "shared/hooks/auth";
import { segundosToHHMMSS } from "shared/helpers/horasHHMM";
import {
  atualizarApontamento,
  criarApontamento,
  excluirApontamento,
  IApontamentoHora,
  ICriarApontamentoRequest,
  IResumoApontamentos,
  ISubtarefaDetalhada,
  listarApontamentos,
} from "services/modules/tarefaDetalhe";

interface TabApontamentoHorasProps {
  idTarefa: number;
  subtarefas: ISubtarefaDetalhada[];
  onRefresh: () => void;
  isSubtarefa?: boolean; // Flag para indicar se está exibindo uma sub-tarefa
}

interface FormApontamento {
  idSubtarefa?: number;
  data: string;
  horaInicio: string;
  horaFim: string;
  observacao: string;
}

const formInicial: FormApontamento = {
  idSubtarefa: undefined,
  data: new Date().toISOString().split("T")[0],
  horaInicio: "",
  horaFim: "",
  observacao: "",
};

export const TabApontamentoHoras = ({
  idTarefa,
  subtarefas,
  onRefresh,
  isSubtarefa = false,
}: TabApontamentoHorasProps) => {
  const { openNotification } = useNavigation();
  const { havePermission } = useAuth();

  // Sub-permissão "Editar o tempo das minhas tarefas" (Fase 4 - Tarefa 1.4.5)
  // Regra de compatibilidade: admins passam sempre; quando o token NÃO possui o
  // marcador de esquema (token legado/empresa sem o recurso), libera tudo. Estando
  // sob o esquema, exige a sub-permissão específica.
  const ehAdminTarefas = havePermission(["Master"]); // FASE 04 item 2: "Configuracao" e a role de acesso a tela, nao um perfil administrativo (PO 20/08, F04-3b)
  const estaSobEsquema = havePermission(["Tarefa.Esquema"]);
  const podeEditarTempo =
    ehAdminTarefas ||
    !estaSobEsquema ||
    havePermission(["Tarefa.EditarTempo"]);

  // Estados
  const [isLoading, setIsLoading] = useState(false);
  const [resumo, setResumo] = useState<IResumoApontamentos>({
    totalMinutos: 0,
    totalSegundos: 0,
    totalHoras: "00:00:00",
    apontamentos: [],
  });

  // Modal
  const [modalAberto, setModalAberto] = useState(false);
  const [formData, setFormData] = useState<FormApontamento>(formInicial);
  const [editandoId, setEditandoId] = useState<number | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // ============================================
  // CARREGAMENTO DE DADOS
  // ============================================

  const carregarApontamentos = useCallback(async () => {
    setIsLoading(true);
    try {
      // Para sub-tarefas, usar idSubtarefa; para tarefas, usar idTarefa
      const filtro = isSubtarefa 
        ? { idSubtarefa: idTarefa }  // idTarefa contém o idSubtarefa quando isSubtarefa=true
        : { idTarefa };
      
      const data = await listarApontamentos(filtro);
      setResumo(data);
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao carregar apontamentos",
        type: "error",
      });
    } finally {
      setIsLoading(false);
    }
  }, [idTarefa, isSubtarefa, openNotification]);

  useEffect(() => {
    carregarApontamentos();
  }, [carregarApontamentos]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleOpenModal = (apontamento?: IApontamentoHora) => {
    if (!podeEditarTempo) {
      openNotification({
        isOpen: true,
        message: "Usuário sem permissão: editar o tempo das tarefas.",
        type: "warning",
      });
      return;
    }
    if (apontamento) {
      setEditandoId(apontamento.idTarefaApontamentoHora);
      setFormData({
        idSubtarefa: apontamento.idSubtarefa || undefined,
        data: apontamento.data.split("T")[0],
        horaInicio: apontamento.horaInicio,
        horaFim: apontamento.horaFim,
        observacao: apontamento.observacao || "",
      });
    } else {
      setEditandoId(null);
      setFormData(formInicial);
    }
    setModalAberto(true);
  };

  const handleCloseModal = () => {
    setModalAberto(false);
    setEditandoId(null);
    setFormData(formInicial);
  };

  const handleSave = async () => {
    // Validação
    if (!formData.data || !formData.horaInicio || !formData.horaFim) {
      openNotification({
        isOpen: true,
        message: "Preencha todos os campos obrigatórios",
        type: "warning",
      });
      return;
    }

    // Validar se hora fim é maior que hora início
    if (formData.horaInicio >= formData.horaFim) {
      openNotification({
        isOpen: true,
        message: "A hora final deve ser maior que a hora inicial",
        type: "warning",
      });
      return;
    }

    setIsSaving(true);
    try {
      if (editandoId) {
        // Atualizar
        await atualizarApontamento({
          idApontamento: editandoId,
          data: formData.data,
          horaInicio: formData.horaInicio,
          horaFim: formData.horaFim,
          observacao: formData.observacao || undefined,
        });
        openNotification({
          isOpen: true,
          message: "Apontamento atualizado com sucesso!",
          type: "success",
        });
      } else {
        // Criar
        // Se isSubtarefa=true, sempre usar idSubtarefa (idTarefa contém o idSubtarefa)
        // Caso contrário, usar idTarefa ou idSubtarefa conforme selecionado no form
        const criarRequest = isSubtarefa
          ? {
              idSubtarefa: idTarefa, // idTarefa contém o idSubtarefa quando isSubtarefa=true
              data: formData.data,
              horaInicio: formData.horaInicio,
              horaFim: formData.horaFim,
              observacao: formData.observacao || undefined,
            }
          : {
              idTarefa: formData.idSubtarefa ? undefined : idTarefa,
              idSubtarefa: formData.idSubtarefa,
              data: formData.data,
              horaInicio: formData.horaInicio,
              horaFim: formData.horaFim,
              observacao: formData.observacao || undefined,
            };
        
        await criarApontamento(criarRequest);
        openNotification({
          isOpen: true,
          message: "Apontamento criado com sucesso!",
          type: "success",
        });
      }

      handleCloseModal();
      await carregarApontamentos();
      onRefresh();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao salvar apontamento",
        type: "error",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (idApontamento: number) => {
    if (!podeEditarTempo) {
      openNotification({
        isOpen: true,
        message: "Usuário sem permissão: editar o tempo das tarefas.",
        type: "warning",
      });
      return;
    }
    if (!window.confirm("Deseja excluir este apontamento?")) return;

    try {
      await excluirApontamento(idApontamento);
      openNotification({
        isOpen: true,
        message: "Apontamento excluído com sucesso!",
        type: "success",
      });
      await carregarApontamentos();
      onRefresh();
    } catch (error) {
      openNotification({
        isOpen: true,
        message: "Erro ao excluir apontamento",
        type: "error",
      });
    }
  };

  // Item 6.5: duração de cada apontamento em hh:mm:ss (fonte: segundos do backend;
  // fallback para minutos*60 quando não vier segundos).
  const formatDuracaoApontamento = (ap: IApontamentoHora): string =>
    segundosToHHMMSS(ap.segundos != null ? ap.segundos : (ap.minutos || 0) * 60);

  // ============================================
  // RENDER
  // ============================================

  return (
    <Box>
      {/* Tabela de Apontamentos - Conforme mockup: Responsável, Sub-tarefa, Data, Ações */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ bgcolor: "grey.200" }}>
              <TableCell sx={{ fontWeight: "bold" }}>Responsável</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Sub-tarefa</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Data</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Duração</TableCell>
              <TableCell align="center" sx={{ fontWeight: "bold" }} width={100}>
                Ações
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Carregando...
                </TableCell>
              </TableRow>
            ) : resumo.apontamentos.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  Nenhum apontamento registrado
                </TableCell>
              </TableRow>
            ) : (
              resumo.apontamentos.map((ap) => (
                <TableRow key={ap.idTarefaApontamentoHora} hover>
                  <TableCell>{ap.nomeUsuario}</TableCell>
                  <TableCell>
                    {ap.nomeSubtarefa || "-"}
                  </TableCell>
                  <TableCell>
                    {new Date(ap.data).toLocaleDateString("pt-BR")}{" "}
                    {ap.horaInicio}:{ap.horaFim && ap.horaFim !== ap.horaInicio ? ap.horaFim.substring(0, 2) : ""}
                  </TableCell>
                  {/* Item 6.5: duração do apontamento em hh:mm:ss. */}
                  <TableCell>{formatDuracaoApontamento(ap)}</TableCell>
                  <TableCell align="center">
                    {podeEditarTempo && ap.podeEditar && (
                      <Tooltip title="Editar">
                        <IconButton
                          size="small"
                          sx={{
                            border: "1px solid #f44336",
                            borderRadius: 1,
                            mr: 0.5,
                            color: "#f44336",
                          }}
                          onClick={() => handleOpenModal(ap)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {podeEditarTempo && ap.podeExcluir && (
                      <Tooltip title="Excluir">
                        <IconButton
                          size="small"
                          sx={{
                            border: "1px solid #f44336",
                            borderRadius: 1,
                            color: "#f44336",
                          }}
                          onClick={() => handleDelete(ap.idTarefaApontamentoHora)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
            {/* Item 6.5: total dos apontamentos em hh:mm:ss (backend já formata
                totalHoras; fallback para totalSegundos). */}
            {!isLoading && resumo.apontamentos.length > 0 && (
              <TableRow sx={{ bgcolor: "grey.100" }}>
                <TableCell colSpan={3} align="right" sx={{ fontWeight: "bold" }}>
                  Total
                </TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>
                  {resumo.totalHoras || segundosToHHMMSS(resumo.totalSegundos)}
                </TableCell>
                <TableCell />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Apontamento */}
      <Dialog open={modalAberto} onClose={handleCloseModal} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editandoId ? "Editar Apontamento" : "Novo Apontamento"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Sub-tarefa (opcional)</InputLabel>
                <Select
                  value={formData.idSubtarefa || ""}
                  label="Sub-tarefa (opcional)"
                  onChange={(e) =>
                    setFormData((prev) => ({
                      ...prev,
                      idSubtarefa: e.target.value ? Number(e.target.value) : undefined,
                    }))
                  }
                  disabled={!!editandoId}
                >
                  <MenuItem value="">
                    <em>Tarefa Principal</em>
                  </MenuItem>
                  {subtarefas.map((sub) => (
                    <MenuItem key={sub.idSubtarefa} value={sub.idSubtarefa}>
                      {sub.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Data *"
                type="date"
                fullWidth
                size="small"
                value={formData.data}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, data: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hora Início *"
                type="time"
                fullWidth
                size="small"
                value={formData.horaInicio}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, horaInicio: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Hora Fim *"
                type="time"
                fullWidth
                size="small"
                value={formData.horaFim}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, horaFim: e.target.value }))
                }
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Observação"
                fullWidth
                multiline
                rows={3}
                size="small"
                value={formData.observacao}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, observacao: e.target.value }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={isSaving}
            startIcon={<Save />}
          >
            {editandoId ? "Salvar" : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

